import { Grid, InputAdornment, TextField, styled, Tooltip } from '@mui/material';
import React, { useState } from 'react';

import ExclamationIcon from '../Component/Icons/ExclamationIcon';

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'customStyleProps',
})(({ customStyleProps, error }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? '#d32f2f' : undefined,
    borderWidth: error ? '2px' : '1px',
  },
  ...customStyleProps,
}));

export const CustomGrid = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const NO_SPECIAL_CHARACTERS_ERROR_MESSAGE = 'Cannot contain special characters';
const EMPTY_FIELD_ERROR_MESSAGE = 'Placeholder cannot be empty';

function useTooltipState(hasError, errorMessage) {
  const [isVisible, setIsVisible] = useState(false);

  return {
    isVisible: hasError && !!errorMessage && isVisible,
    show: () => setIsVisible(true),
    hide: () => setIsVisible(false),
    toggle: () => setIsVisible(prev => !prev),
  };
}

function W86PlaceholderTextField({
  placeholderText,
  value,
  defaultValue,
  handleChange,
  error,
  helperText,
  onBlur,
  size,
  className,
  disabled,
  customStyleProps,
  fullWidth = false,
  disableAdornment = false,
  excludePrefix = '',
}) {
  const [errMessage, setErrMessage] = useState('');

  const displayValue =
    value !== undefined
      ? value && excludePrefix && value.startsWith(excludePrefix)
        ? value.slice(excludePrefix.length)
        : value
      : defaultValue;

  const hasError = !displayValue || !!errMessage || !!error;

  const displayErrorMessage = !displayValue
    ? EMPTY_FIELD_ERROR_MESSAGE
    : errMessage || helperText || '';

  const { isVisible, show, hide } = useTooltipState(hasError, displayErrorMessage);

  const handleInputChange = e => {
    const inputValue = e.target.value;

    const containsInvalidChars = /[^a-zA-Z\d\s:_]/.test(inputValue);

    if (containsInvalidChars) {
      setErrMessage(NO_SPECIAL_CHARACTERS_ERROR_MESSAGE);
      show();
    } else {
      setErrMessage('');
    }

    const sanitizedValue = inputValue.replace(/\s/g, '_').replace(/[^a-zA-Z\d:_]/g, '');

    const newValue = excludePrefix + sanitizedValue;
    handleChange({ target: { value: newValue } });
  };

  const handleFieldBlur = e => {
    hide();
    if (onBlur) onBlur(e);
  };

  const handleFieldFocus = () => {
    if (hasError && !!displayErrorMessage) {
      show();
    }
  };

  return (
    <Tooltip
      title={displayErrorMessage}
      arrow
      placement="top"
      open={isVisible}
      disableFocusListener
      disableHoverListener
      disableTouchListener
    >
      <StyledTextField
        id="w86-placeholder-textfield"
        customStyleProps={customStyleProps}
        className={className || ''}
        disabled={disabled ? disabled : false}
        size={size ? size : 'small'}
        variant="outlined"
        fullWidth={fullWidth}
        value={displayValue}
        error={hasError}
        onChange={handleInputChange}
        onBlur={handleFieldBlur}
        onFocus={handleFieldFocus}
        placeholder={placeholderText}
        InputProps={{
          startAdornment: !disableAdornment && (
            <InputAdornment position="start">{'${'}</InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Grid container horizontal="center" vertical="center">
                <CustomGrid item>
                  {!disableAdornment && '}'}
                  {hasError && <ExclamationIcon style={{ marginLeft: '4px' }} />}
                </CustomGrid>
              </Grid>
            </InputAdornment>
          ),
        }}
      />
    </Tooltip>
  );
}

export default W86PlaceholderTextField;
